import { useMemo } from 'react';
import { useEntityRelationships, EntityReference } from './useEntityRelationships';
import { useTextHighlighting } from '@/providers/TextHighlightingProvider';

interface LiveRelationship {
  target_id: string;
  target_type: string;
  relationship_context?: string;
  isLive?: boolean; // Indicates this is detected live, not saved yet
}

interface CombinedRelationships {
  referenced_by: EntityReference[];
  references: LiveRelationship[];
  referenced_by_count: number;
  references_count: number;
  live_references_count: number;
}

/**
 * Hook that combines saved relationships with live detected relationships from highlighting
 * @param entityId - The ID of the entity
 * @param entityType - The type of the entity
 * @param gameId - The game ID
 * @param detectLiveReferences - Whether to detect live references from highlighting (default: false)
 */
export function useLiveEntityRelationships(
  entityId: string | undefined,
  entityType: string | undefined,
  gameId?: string,
  detectLiveReferences: boolean = false
) {
  // Get saved relationships from server
  const { relationships, loading, error } = useEntityRelationships(
    entityId,
    entityType,
    gameId
  );

  // Get live highlight items if we're detecting live references
  const { highlightItems } = useTextHighlighting();

  // Combine saved and live relationships
  const combinedRelationships = useMemo((): CombinedRelationships | null => {
    if (!relationships) return null;

    // If not detecting live references, return saved relationships as-is
    if (!detectLiveReferences) {
      return {
        ...relationships,
        references: relationships.references.map(ref => ({
          ...ref,
          isLive: false
        })),
        live_references_count: 0
      };
    }

    // Extract live references from highlight items
    const liveReferences: LiveRelationship[] = highlightItems
      .filter(item => {
        // Only include glossary and type items as references
        return item.type === 'glossary' || item.type === 'Library Object' || item.type === 'type';
      })
      .map(item => ({
        target_id: item.id,
        target_type: item.type,
        relationship_context: 'live_highlight',
        isLive: true
      }));

    // Get saved reference IDs for deduplication
    const savedReferenceIds = new Set(
      relationships.references.map(ref => `${ref.target_type}:${ref.target_id}`)
    );

    // Filter out live references that are already saved
    const newLiveReferences = liveReferences.filter(
      ref => !savedReferenceIds.has(`${ref.target_type}:${ref.target_id}`)
    );

    // Combine saved references with new live references
    const allReferences = [
      ...relationships.references.map(ref => ({ ...ref, isLive: false })),
      ...newLiveReferences
    ];

    return {
      referenced_by: relationships.referenced_by,
      references: allReferences,
      referenced_by_count: relationships.referenced_by_count,
      references_count: allReferences.length,
      live_references_count: newLiveReferences.length
    };
  }, [relationships, highlightItems, detectLiveReferences]);

  return {
    relationships: combinedRelationships,
    loading,
    error,
    hasLiveReferences: (combinedRelationships?.live_references_count || 0) > 0
  };
}


